// Keep this module dependency-light (React only at runtime): it is consumed by
// the media settings factory, and importing the provider component from there
// creates a module-initialization cycle through the settings hooks barrel.
import {createContext, useContext} from 'react';

import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {type DispatchableAction} from '../../../types/redux';
import {
  type SectionContextInheritedSettings,
  type SectionContextMediaSettings,
} from './sectionSettingsContextTypes';
import {type Complete, type LinePlotSettings} from './types';

export type SectionSettingsContextType = {
  /**
   * Set of settings that are either inherited from workspace or defined at the section level.
   * Media settings are not included; use per-field hooks to read them.
   */
  inheritedSettings: SectionContextInheritedSettings;
  /**
   * Section level settings defined in view spec
   */
  linePlot: Complete<LinePlotSettings>;
  sectionRef: SectionRef;
  updateLinePlotSectionSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  /** Raw section-level media settings from view spec (migrated fields omitted from type). */
  sectionMediaSettings: SectionContextMediaSettings;
};

export const SectionSettingsContext = createContext<
  SectionSettingsContextType | undefined
>(undefined);

export const useSectionSettingsContext = (): SectionSettingsContextType => {
  const value = useContext(SectionSettingsContext);

  if (value == null) {
    throw new Error(
      'useSectionSettingsContext must be used within a SectionSettingsContextProvider'
    );
  }

  return value;
};

export const useSectionSettingsContextMaybe = () => {
  return useContext(SectionSettingsContext);
};
